import { useEffect, useState } from 'react';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';

// Theme + primitives ported directly from the reference screens in
// `new screen/*.jsx` (CommitmentsTracker / DependencyBlockerTracker /
// RisksTracker) so the register looks exactly like those mocks, not a
// reinterpretation in the app's existing zinc palette.

export const T = {
  bg1: '#18181b', // card / container surface (zinc-900)
  bg2: '#09090b', // recessed / nested surface (zinc-950)
  bg3: '#060606', // app background
  text: '#f4f4f5', // zinc-100
  textSec: '#d4d4d8', // zinc-300
  muted: '#a1a1aa', // zinc-400
  br: '#27272a', // zinc-800
  primary: '#5652CF',
  primaryBg: 'rgba(86,82,207,0.15)',
  primaryHover: '#4642bf',
  danger: '#f87171',
  dangerBg: 'rgba(248,113,113,0.13)',
  warn: '#fbbf24',
  warnBg: 'rgba(251,191,36,0.13)',
  ok: '#4ade80',
  okBg: 'rgba(74,222,128,0.13)',
  info: '#38bdf8',
  infoBg: 'rgba(56,189,248,0.12)',
  decision: '#c084fc',
  decisionBg: 'rgba(192,132,252,0.13)',
  selected: 'rgba(86,82,207,0.15)',
};

export const Chip = ({
  children,
  fg = T.textSec,
  bg = 'rgba(212,212,216,0.08)',
  bordered = false,
  // Opt-in, not default — most chips (status, risk type) read fine in
  // their natural case; this matches the uppercase/tracking-wide label
  // style used elsewhere (SectionLabel, the GanttDetailPanel stat grid)
  // for the specific chips a caller wants to read as a label, not a value.
  uppercase = false,
}) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium leading-4 shadow-sm ${uppercase ? 'uppercase tracking-wide' : ''}`}
    style={{
      color: fg,
      background: bg,
      border: bordered ? `1px solid ${T.br}` : '1px solid transparent',
    }}
  >
    {children}
  </span>
);

export const Field = ({ label, value, tone }) => (
  <div className="shadow-xs min-w-0 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5">
    <div className="text-[11px] font-medium tracking-wide text-zinc-400">
      {label}
    </div>
    <div
      className="mt-0.5 break-words text-[13px] font-semibold text-zinc-100"
      style={{ color: tone || undefined }}
    >
      {value ?? '—'}
    </div>
  </div>
);

/** One cell in a consolidated stat grid — no border/bg of its own; the
 * grid itself carries one shared border/bg instead of each field having
 * its own (the treatment InsightsGantt's, Commitment's, and Risk/Concern's
 * detail panels all use for their Status/Owner/Cause/... stats). Renders
 * nothing when there's no value, rather than a "—" placeholder cell;
 * `title` gives the full text on hover for values `truncate` would
 * otherwise cut off. Distinct from `Field` above, which still owns its
 * own card and is kept for the one caller not yet moved to this pattern.
 */
export const StatItem = ({ label, value, tone }) => {
  if (value == null || value === '') return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
        {label}
      </div>
      <div
        className="mt-0.5 truncate text-[13px] font-semibold text-zinc-100"
        style={{ color: tone || undefined }}
        title={String(value)}
      >
        {value}
      </div>
    </div>
  );
};

export const SectionLabel = ({ children, count }) => (
  <div className="mb-2.5 flex items-center gap-2">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
      {children}
    </span>
    {count != null && (
      <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] font-bold text-zinc-300">
        {count}
      </span>
    )}
  </div>
);

export const Btn = ({ children, onClick, variant = 'default', disabled }) => {
  const isPrimary = variant === 'primary';
  const isDanger = variant === 'danger';
  const isGhost = variant === 'ghost';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl px-4 py-2 text-[12px] font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/50 disabled:cursor-not-allowed disabled:opacity-40 ${
        isPrimary
          ? 'bg-primary-gradient text-white shadow-lg shadow-indigo-950/40 hover:brightness-110'
          : isDanger
            ? 'border border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25'
            : isGhost
              ? 'bg-transparent text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
              : 'border border-zinc-700/80 bg-zinc-800/80 text-zinc-200 shadow-sm hover:bg-zinc-700/80'
      }`}
    >
      {children}
    </button>
  );
};

export const EmptyNote = ({ children }) => (
  <div className="text-[13px] text-zinc-400">{children}</div>
);

export const EmptyState = ({ icon: Icon, children }) => (
  <div
    className="flex flex-col items-center gap-3 rounded-2xl py-16 text-center backdrop-blur-sm"
    style={{ background: T.bg1, border: `1px solid ${T.br}` }}
  >
    {Icon && <Icon className="h-9 w-9 text-zinc-500" />}
    <p className="max-w-sm text-[13px] text-zinc-400">{children}</p>
  </div>
);

export const Spinner = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 className="h-8 w-8 animate-spin text-[#5652CF]" />
  </div>
);

export const ErrorBanner = ({ children }) =>
  children ? (
    <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-[13px] text-red-300 shadow-sm">
      {children}
    </div>
  ) : null;

export const humanise = (s) => {
  if (!s) return '—';
  return String(s).replace(/_/g, ' ').toLowerCase();
};

/**
 * committer_person_id / responsible_person_id / accountable_person_id /
 * raiser_person_id etc. fall back to an email whenever a workspace has no
 * Teams or Jira mapping for that person -- there is no separate "display
 * name" field on these records to read instead. Printing the raw email in a
 * scan-first list (register rows) or headline (detail panels) reads as
 * broken data next to entries where a resolved name is available, even
 * though it's the same field just missing a mapping. Mirrors
 * app/config/meetings/digest.py's `_person()` on the backend -- same
 * fallback, same reasoning, kept in sync deliberately rather than plumbing a
 * whole participant-directory lookup through every register view for what
 * is, worst case, a cosmetic capitalisation of the part before the @.
 */
export const personLabel = (personId) => {
  if (!personId) return null;
  const text = String(personId);
  if (!text.includes('@')) return text;
  const local = text.split('@', 1)[0];
  return local
    .replace(/\./g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/**
 * Raw entity_id/generation-id style values (concern_entities, commitments,
 * decisions, change_events -- every live record from the meeting pipeline)
 * are a bare uuid4().hex: 32 lowercase hex characters, chosen in
 * stage_four/computation.py as an internal database key, never as a label a
 * person is meant to read. Printing the full 32 characters as a row's
 * headline is what makes live rows look inconsistent next to anything with
 * a short, readable id. Truncating is display-only -- the full id still
 * goes out on click (openDetail/getCommitment etc. use the untouched
 * value), so nothing here can break a lookup.
 */
export const shortRef = (id) => {
  if (!id) return null;
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
};

/**
 * concern_entities.mitigation_actions/discussions are documented as
 * `list[str]`, but real data committed before that migration still has
 * entries shaped like `{action_text, owner_person_id, due_date,
 * followup_channel, ...}`. Rendering an object directly as a JSX child
 * throws (React #31), so accept either shape here rather than trusting the
 * schema blindly. Stage 4b's current code only ever appends plain strings
 * (stage_four/computation.py, stage_two/concerns/analyzer.py) -- this is
 * for legacy documents, not a live extraction bug.
 */
export const concernEntryText = (entry) => {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') {
    const owner = entry.owner_person_id || entry.accountable_person_id;
    const due = entry.due_date ? ` (due ${entry.due_date})` : '';
    return `${entry.action_text || entry.text || entry.origin || 'Mitigation action'}${owner ? ` · ${owner}` : ''}${due}`;
  }
  return String(entry);
};

export const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

export const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const relativeAge = (isoDate) => {
  if (!isoDate) return null;
  const days = Math.floor(
    (Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

export const criticalityTone = (c) => {
  if (c === 'CRITICAL' || c === 'HIGH') return T.danger;
  if (c === 'MEDIUM') return T.warn;
  if (c === 'LOW') return T.textSec;
  return T.muted;
};

/** RiskV1's `risk_type` used as a row/detail accent chip. */
export const RISK_TYPE_TONE = {
  TECHNICAL: { fg: '#7dd3fc', bg: 'rgba(125,211,252,0.13)' },
  TIMELINE: { fg: T.warn, bg: T.warnBg },
  CLIENT: { fg: T.decision, bg: T.decisionBg },
  STAKEHOLDER: { fg: T.decision, bg: T.decisionBg },
  BUDGET: { fg: T.ok, bg: T.okBg },
  SCOPE: { fg: T.warn, bg: T.warnBg },
};

const CLOSED_STATES = ['CLOSED'];

/** A concern is closed once the tracker's embedded watch state says so. */
export const isClosed = (concern) =>
  CLOSED_STATES.includes(concern?.tracker?.state);

/** The label a closed concern's badge shows: its last terminal history entry. */
export const closureLabel = (concern) => {
  const terminal = ['RESOLVED', 'MATERIALISED', 'DISMISSED'];
  const entries = concern?.history || [];
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (terminal.includes(entries[i]?.update_type))
      return entries[i].update_type;
  }
  return concern?.tracker?.closure_reason || null;
};

export const statusTone = (label) => {
  switch (label) {
    case 'RESOLVED':
      return { fg: T.ok, bg: T.okBg, label: 'Resolved' };
    case 'DISMISSED':
      return { fg: T.muted, bg: 'rgba(135,135,127,0.12)', label: 'Dismissed' };
    case 'MATERIALISED':
      return { fg: T.danger, bg: T.dangerBg, label: 'Materialised' };
    case 'ESCALATED':
      return { fg: T.danger, bg: T.dangerBg, label: 'Escalated' };
    case 'MITIGATING':
      return { fg: T.info, bg: T.infoBg, label: 'Mitigating' };
    // Admission state, not resolution state (see concernStatusLabel) -- a
    // concern the pipeline hasn't confirmed yet, or confirmed-but-suppressed
    // and kept only for corroboration (stage_five/service.py._tracker_handoff).
    case 'HYPOTHESIS':
      return { fg: T.info, bg: T.infoBg, label: 'Hypothesis' };
    case 'SUPPRESSED':
      return { fg: T.muted, bg: 'rgba(135,135,127,0.12)', label: 'Suppressed' };
    default:
      return {
        fg: T.textSec,
        bg: 'rgba(209,209,199,0.08)',
        label: label ? humanise(label) : 'Open',
      };
  }
};

/**
 * The label to feed `statusTone` for a concern row/detail badge.
 *
 * Priority: a closed concern's terminal history entry, then `tracker.state`
 * saying CLOSED, then `lifecycle_stage` when it's HYPOTHESIS or SUPPRESSED
 * (an admission-state distinct from the resolution states below -- a
 * SUPPRESSED concern is still committed and returned by `/concerns`, just
 * not tracked, so without this it renders identically to a live one). Falls
 * back to `concern.status` -- the resolution-axis field (OPEN, MITIGATING,
 * RESOLVED, ESCALATED, MATERIALISED, DISMISSED).
 */
export const concernStatusLabel = (concern) => {
  const closed = closureLabel(concern);
  if (closed) return closed;
  if (isClosed(concern)) return 'CLOSED';
  if (['HYPOTHESIS', 'SUPPRESSED'].includes(concern?.lifecycle_stage)) {
    return concern.lifecycle_stage;
  }
  return concern?.status;
};

/**
 * List-to-detail navigation: click a row, fetch its detail. Shared by every
 * register tab that has a detail view.
 */
export const useDetailNav = (fetcher) => {
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const open = async (entityId) => {
    setSelectedId(entityId);
    setDetail(null);
    setError(null);
    setLoading(true);
    try {
      const response = await fetcher(entityId);
      setDetail(response);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setSelectedId(null);
    setDetail(null);
    setError(null);
  };

  return { selectedId, detail, setDetail, loading, error, open, close };
};

/**
 * Jira-ticket-style detail: slides in from the right over the list, instead
 * of navigating away from it. `open` controls both the backdrop and the
 * slide transition; the panel unmounts on close (no exit animation).
 */
export const DrawerDetail = ({ open, onClose, children }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close detail panel"
        onClick={onClose}
        className={`fixed inset-0 z-40 cursor-default border-none bg-black/25 p-0 transition-opacity duration-300 ${entered ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-xl overflow-y-auto border-l border-zinc-700/80 bg-[#18181b] text-zinc-100 shadow-2xl transition-transform duration-300 ease-out ${entered ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {children}
      </div>
    </>
  );
};

/**
 * The one detail-drawer layout every register tab uses: eyebrow id + type
 * chips + close button, a title, an optional "raised in" quote block, then
 * scrollable body content, then an optional action footer. Every detail
 * panel (Commitment/Decision/Concern) is built from this so they read as
 * one product instead of three different layouts.
 */
export const DetailShell = ({
  eyebrow,
  chips,
  statusChip,
  title,
  raisedIn,
  quote,
  onClose,
  children,
  footer,
}) => (
  <div className="flex h-full flex-col bg-[#18181b] text-zinc-100">
    <div className="shrink-0 border-b border-zinc-700/80 bg-[#18181b] px-5 pb-3 pt-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {eyebrow && (
            <span className="text-[11px] font-medium text-zinc-400">
              {eyebrow}
            </span>
          )}
          {chips}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {statusChip}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {title && (
        <h2 className="mt-2.5 text-[17px] font-bold leading-snug text-zinc-100">
          {title}
        </h2>
      )}
    </div>

    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
      {raisedIn && (
        <div className="mb-3">
          <SectionLabel>Raised in</SectionLabel>
          <p className="text-[13px] font-medium text-zinc-200">{raisedIn}</p>
          {quote && (
            <div className="mt-2 rounded-xl border border-indigo-500/30 bg-indigo-950/30 px-3 py-2.5 text-[13px] italic leading-relaxed text-zinc-300 shadow-sm">
              &ldquo;{quote}&rdquo;
            </div>
          )}
        </div>
      )}
      {children}
    </div>

    {footer && (
      <div className="shrink-0 border-t border-zinc-700/80 bg-[#18181b] px-5 py-3">
        {footer}
      </div>
    )}
  </div>
);

/** Search box + optional filter <select>s, shared across the five list panes. */
export const FilterBar = ({
  search,
  onSearchChange,
  placeholder,
  children,
}) => (
  <div className="mb-2 flex flex-wrap items-center gap-2">
    <div className="relative min-w-[200px] flex-1">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
      <input
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-zinc-700/80 bg-zinc-900/90 py-2 pl-9 pr-3.5 text-[12px] text-zinc-100 outline-none transition-all placeholder:text-zinc-500 focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]/20"
      />
    </div>
    {children}
  </div>
);

// Native <select> arrows don't reliably respect padding across browsers —
// some render flush against the box's right edge regardless. Hiding the
// native one (appearance-none) and drawing our own with real pr-8 spacing
// guarantees consistent placement instead of relying on browser default.
export const FilterSelect = (props) => (
  <div className="relative">
    <select
      {...props}
      className="w-full appearance-none rounded-xl border border-zinc-700/80 bg-zinc-900/90 py-2 pl-3 pr-8 text-[12px] text-zinc-200 outline-none transition-all focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF]/20"
    />
    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
  </div>
);
